import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { lazyWithTiming } from './utils/lazyWithTiming';
import { initializePSGCDatabase } from './utils/psgc';

// Pages
const LandingPage = lazyWithTiming(() => import('./pages/LandingPage'), 'LandingPage');
const RegistrationForm = lazyWithTiming(() => import('./pages/RegistrationForm'), 'RegistrationForm');
const DraftSaved = lazyWithTiming(() => import('./pages/DraftSaved'), 'DraftSaved');
const SubmissionThankYou = lazyWithTiming(() => import('./pages/SubmissionThankYou'), 'SubmissionThankYou');
const EmployeeLogin = lazyWithTiming(() => import('./pages/EmployeeLogin'), 'EmployeeLogin');
const ApplicationsList = lazyWithTiming(() => import('./pages/EmployeeDashboard'), 'ApplicationsList');
const ApplicationDetail = lazyWithTiming(() => import('./pages/ApplicationDetail'), 'ApplicationDetail');
const EmployeeManagement = lazyWithTiming(() => import('./pages/EmployeeManagement'), 'EmployeeManagement');
const NotFound = lazyWithTiming(() => import('./pages/NotFound'), 'NotFound');

function App() {
  useEffect(() => {
    // Initialize PSGC database on app startup (non-blocking)
    // LandingPage will do more aggressive prefetching
    initializePSGCDatabase().catch(error => {
      console.error('❌ Failed to initialize PSGC database:', error);
      // Don't block app startup
    });
  }, []);

  // Use environment variable for basename, default to empty string for development
  const basename = process.env.REACT_APP_BASENAME || '';

  return (
    <AuthProvider>
      <Router basename={basename}>
        <div className="App">
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          <Suspense fallback={<div className="p-6 text-gray-600">Loading...</div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/register" element={<RegistrationForm />} />
              <Route path="/draft-saved" element={<DraftSaved />} />
              <Route path="/thank-you" element={<SubmissionThankYou />} />
              <Route path="/login" element={<EmployeeLogin />} />

              {/* Protected employee routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Navigate to="/dashboard/applications" />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/applications" element={
                <ProtectedRoute>
                  <ApplicationsList />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/applications/:id" element={
                <ProtectedRoute>
                  <ApplicationDetail />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/employees" element={
                <ProtectedRoute requireAdmin>
                  <EmployeeManagement />
                </ProtectedRoute>
              } />

              {/* 404 Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
